import { useState, useRef, useEffect } from "react";
import Axios from '@/shared/api/axios';
import MessageModal from '@/components/ui/MessageModal';
import StatusModal from '@/components/ui/StatusModal';
import LoadingButton from '@/components/ui/LoadingButton';
import DatePicker from '@/components/ui/DatePicker';
import { useTranslation } from 'react-i18next';
import { toDateInputValue } from '@/shared/utils/formatDate';

function AccountSettings() {
  const [info, setInfo] = useState(null);
  const [pwdInfo, setPwdInfo] = useState(null);
  const { t } = useTranslation();

  const passwordRef = useRef(null);
  const newPasswordRef = useRef(null);

  const modal1000 = useRef(null);
  const modal1001 = useRef(null);
  const modal2021 = useRef(null);
  const modal2023 = useRef(null);
  const modal2024 = useRef(null);
  const modal2032 = useRef(null);
  const statusModalRef = useRef(null);

  const [currentStatusCode, setCurrentStatusCode] = useState('');
  const [isSavingInfo, setIsSavingInfo] = useState(false);
  const [isSavingPwd, setIsSavingPwd] = useState(false);

  const getInfo = async() => {
    try {
      const res = await Axios.get("/user/get-info");
      switch(res.data.status_code){
        case 'SYSTEM_1000':
            setInfo(res.data.data);
            break;
        default:
            setCurrentStatusCode(res.data.status_code);
            statusModalRef.current?.show();
            break
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Get user info error:', error);
      }
    }
  };

  const handleChange = e => {
      setInfo(({
          ...info,
          [e.target.name]: e.target.value,
      }))
  }

  const handlePwdChange = e => {
    setPwdInfo(({
        ...pwdInfo,
        [e.target.name]: e.target.value,
    }))
}

  const handleUpdateInfo = async(e) => {
    e.preventDefault();
    setIsSavingInfo(true);
    try {
      const res = await Axios.post("/user/update-info", info);
      switch(res.data.status_code){
        case 'SYSTEM_1000':
            modal1000.current.show();
            break;
        case 'SYSTEM_1001':
            modal1001.current.show();
            break
        case 'USER_2021':
            modal2021.current.show();
            break
        case 'USER_2024':
            modal2024.current.show();
            break
        default:
            setCurrentStatusCode(res.data.status_code);
            statusModalRef.current?.show();
            break
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      }
    } finally {
      setIsSavingInfo(false);
    }
  };

  const handleResetPwd = async(e) => {
    e.preventDefault();
    setIsSavingPwd(true);
    try {
      const res = await Axios.post("/user/reset-password", pwdInfo);
      switch(res.data.status_code){
        case 'SYSTEM_1000':
            modal1000.current.show();
            passwordRef.current.value = "";
            newPasswordRef.current.value = "";
            break;
        case 'SYSTEM_1001':
            modal1001.current.show();
            break
        case 'USER_2023':
            modal2023.current.show();
            break
        case 'USER_2032':
            modal2032.current.show();
            break
        default:
            setCurrentStatusCode(res.data.status_code);
            statusModalRef.current?.show();
            break
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      }
    } finally {
      setIsSavingPwd(false);
    }
  };

  useEffect(() => {
    getInfo();
  },[])

    return (
        <>
            <h3 className="mb-4">{t('profile')}</h3>
            <div className="bg-white rounded p-4 mb-4">
              <h5 className="mb-4">{t('edit_profile')}</h5>
              <form className="row g-2"
              onSubmit={ handleUpdateInfo }>
                <div className="col-md-6">
                  <label htmlFor="username" className="form-label">{t('username')}</label>
                  <input type="text" className="form-control" name="username" id="username"
                  onChange={handleChange} value={info?.username || ''}/>
                </div>
                <div className="col-md-6">
                  <label htmlFor="birthday" className="form-label">{t('birthday')}</label>
                  <DatePicker id="birthday" name="birthday"
                  value={info?.birthday || ''}
                  onChange={(date) => setInfo({ ...info, birthday: date ? toDateInputValue(date) : '' })}/>
                </div>
                <div className="col-md-6">
                  <label htmlFor="email" className="form-label">{t('email')}</label>
                  <input type="email" className="form-control" name="email" id="email"
                  value={info?.email || ''} disabled/>
                </div>
                <div className="col-12 text-end">
                  <LoadingButton type="submit" className="btn btn-primaryF link-light rounded-pill"
                  isLoading={isSavingInfo}>{t('save')}</LoadingButton>
                </div>
              </form>
            </div>
            <div className="bg-white rounded p-4 mb-4">
              <h5 className="mb-4">{t('change_password')}</h5>
              <form className="row g-2"
              onSubmit={ handleResetPwd }>
                <div className="col-md-6">
                  <label htmlFor="password" className="form-label">{t('password')}</label>
                  <input type="password" className="form-control" name="password" id="password" autoComplete="new-password"
                  onChange={handlePwdChange} ref={passwordRef}/>
                </div>
                <div className="col-md-6">
                  <label htmlFor="newPassword" className="form-label">{t('new_password')}</label>
                  <input type="password" className="form-control" name="new_password" id="newPassword" autoComplete="new-password"
                  onChange={handlePwdChange} ref={newPasswordRef}/>
                </div>
                <div className="col-12 text-end">
                  <LoadingButton type="submit" className="btn btn-primaryF link-light rounded-pill" isLoading={isSavingPwd}>{t('save')}</LoadingButton>
                </div>
              </form>
            </div>

            <MessageModal ref={modal1000} status="success">
                <p>{t('account_success')}</p>
            </MessageModal>
            <MessageModal ref={modal1001} status="error">
              <p>{t('account_error1')}</p>
            </MessageModal>
            <MessageModal ref={modal2021} status="error">
                <p>{t('account_error2')}</p>
            </MessageModal>
            <MessageModal ref={modal2023} status="error">
                <p>{t('account_error3')}</p>
            </MessageModal>
            <MessageModal ref={modal2024} status="error">
                <p>{t('account_error4')}</p>
            </MessageModal>
            <MessageModal ref={modal2032} status="error">
                <p>{t('account_error5')}</p>
            </MessageModal>

            <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
        </>
    );
}

export default AccountSettings;